/**
 * Decisão sobre o saldo do PO anterior.
 *
 * O saldo do período anterior deixou de ser incorporado automaticamente: o
 * sistema calcula o valor e pergunta. Enquanto não houver decisão, nenhum
 * lançamento existe no período.
 *
 * A recusa é DEFINITIVA para o período — nem uma alteração posterior no PO
 * anterior traz o valor de volta. Por isso o modal avisa disso antes, e não
 * fecha sozinho: sair por Esc ou clique fora seria uma decisão acidental sobre
 * dinheiro. Só os dois botões encerram a pergunta.
 */
import { Loader2, ArrowDownRight, ArrowUpRight } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CurrencyDisplay } from '@/components/financial/CurrencyDisplay';
import {
  useAcceptCarryover,
  useDeclineCarryover,
  usePendingCarryover,
} from '@/hooks/use-financial';

interface CarryoverDecisionModalProps {
  periodId?: string;
}

export function CarryoverDecisionModal({ periodId }: Readonly<CarryoverDecisionModalProps>) {
  const { data: pending } = usePendingCarryover(periodId);
  const { mutate: aceitar, isPending: aceitando } = useAcceptCarryover();
  const { mutate: recusar, isPending: recusando } = useDeclineCarryover();

  const decidindo = aceitando || recusando;

  if (!periodId || !pending) return null;

  const ehReceita = pending.kind === 'revenue';
  const Icone = ehReceita ? ArrowUpRight : ArrowDownRight;

  return (
    <Dialog open>
      <DialogContent
        // A decisão é obrigatória: sem botão de fechar, sem Esc, sem clique fora.
        hideCloseButton
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Saldo do período anterior</DialogTitle>
          <DialogDescription>
            O período anterior fechou com saldo. Você quer incluí-lo neste período?
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
          <div className="flex items-center gap-2">
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                ehReceita
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
              }`}
            >
              <Icone className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="text-sm font-medium text-foreground">
              {ehReceita ? 'Receita' : 'Despesa'}
            </span>
          </div>

          <div className="mt-3">
            <CurrencyDisplay
              value={pending.amount}
              size="lg"
              className="text-2xl font-bold"
              colorize={false}
            />
          </div>

          <p className="mt-3 text-xs leading-snug text-muted-foreground">
            {ehReceita
              ? 'Se incluir, o valor entra como uma receita única deste período.'
              : 'Se incluir, o valor entra como uma despesa mensal única deste período.'}{' '}
            Se recusar, não será possível incluí-lo depois — a escolha vale para este período.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={decidindo}
            onClick={() => recusar(periodId)}
          >
            {recusando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Não incluir
          </Button>
          <Button type="button" disabled={decidindo} onClick={() => aceitar(periodId)}>
            {aceitando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Incluir no período
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
